// Polyfill HTMLFormElement.requestSubmit() for Safari < 16 / iOS < 16
if (typeof HTMLFormElement !== "undefined" && !HTMLFormElement.prototype.requestSubmit) {
  HTMLFormElement.prototype.requestSubmit = function (submitter) {
    if (submitter) {
      if (submitter.type !== "submit")
        throw new TypeError("The specified element is not a submit button.")
      if (submitter.form !== this)
        throw new DOMException(
          "The specified element is not owned by this form element.",
          "NotFoundError",
        )
      submitter.click()
    } else {
      submitter = document.createElement("input")
      submitter.type = "submit"
      submitter.hidden = true
      this.appendChild(submitter)
      submitter.click()
      this.removeChild(submitter)
    }
  }
}

// Popover API for Safari < 17 / iOS < 17. The date and time pickers, the Baustellen cell editors
// and the planning dialogs all use showPopover() and `:popover-open`; without it every open
// throws "The string did not match the expected pattern" and the panels render permanently
// visible. Applies itself only where the API is missing, and patches matches()/querySelector()
// so `:popover-open` keeps working in JS.
import "@oddbird/popover-polyfill"

// Initialize Honeybadger error tracking
import "./honeybadger"

import "@hotwired/turbo-rails"

// Never let Turbo prefetch a file link. Active Storage URLs are same-origin but redirect to the
// S3 bucket, and Turbo's prefetch sends custom headers (X-Turbo-Request-Id, X-Sec-Purpose), so
// following that redirect needs a CORS preflight the bucket rejects. The prefetch rejects with
// "Failed to fetch" as an unhandled promise — reported on every tap of a photo thumbnail in a
// WebView, where a tap also fires mouseenter. The file is not a page, so there is nothing to gain.
document.addEventListener("turbo:before-prefetch", (event) => {
  const href = event.target.getAttribute("href") || ""
  if (href.includes("/rails/active_storage/")) event.preventDefault()
})

// A frame request answered with an error page (its record was deleted while the page sat open)
// shows a notice in the frame instead of an endless skeleton and an unhandled rejection.
import { handleFrameErrorResponse } from "./helpers/turbo_frame_error"
document.addEventListener("turbo:frame-missing", handleFrameErrorResponse)

// Custom stream action: `turbo_stream.action(:reload_frame, frame_id)` refetches a frame in place.
import { Turbo } from "@hotwired/turbo-rails"
import { reloadFrame } from "./helpers/reload_frame_stream_action"
Turbo.StreamActions.reload_frame = reloadFrame
// `position_grid_sync`: only the changed rows of a position grid after a structural step.
import { syncPositionGrid } from "./helpers/position_grid_sync_stream_action"
Turbo.StreamActions.position_grid_sync = syncPositionGrid

// Replace Turbo's native confirm() with a custom on-brand dialog (app-wide).
import "./turbo_confirm"

import "trix"

// Guard against actiontext dispatchEvent error with Turbo Drive
// @rails/actiontext's trix-attachment-add handler expects event.target
// to be a DOM Element, but it can be null/stale during Turbo cache cycles
document.addEventListener(
  "trix-attachment-add",
  (event) => {
    if (!event.target || typeof event.target.dispatchEvent !== "function") {
      event.stopImmediatePropagation()
    }
  },
  true,
)

import "@rails/actiontext"

// Registers the <calendar-date>/<calendar-range>/<calendar-month> custom elements used by
// the date-picker controller.
import "cally"

import "./channels"
import "./controllers"

// Global SlimSelect iOS/WKWebView touch compatibility.
// Flutter's WKWebView does not reliably synthesize click events from touch,
// which breaks SlimSelect's click-based handlers. These global listeners
// bridge touch→click for ALL SlimSelect instances regardless of controller.
;(function () {
  const TOUCH_MOVE_THRESHOLD = 10
  let touchStartX = 0
  let touchStartY = 0
  let touchMoved = false

  document.addEventListener(
    "touchstart",
    (e) => {
      const touch = e.touches[0]
      touchStartX = touch.clientX
      touchStartY = touch.clientY
      touchMoved = false
    },
    { passive: true },
  )

  document.addEventListener(
    "touchmove",
    (e) => {
      if (touchMoved) return
      const touch = e.touches[0]
      const dx = Math.abs(touch.clientX - touchStartX)
      const dy = Math.abs(touch.clientY - touchStartY)
      if (dx > TOUCH_MOVE_THRESHOLD || dy > TOUCH_MOVE_THRESHOLD) {
        touchMoved = true
      }
    },
    { passive: true },
  )

  document.addEventListener(
    "touchend",
    (e) => {
      // If the finger moved significantly, this is a scroll — not a tap
      if (touchMoved) return

      // Remove selected value when tapping the delete (X) button
      const ssDelete = e.target.closest(".ss-value-delete")
      if (ssDelete) {
        e.preventDefault()
        e.stopPropagation()
        ssDelete.click()
        return
      }

      // Toggle dropdown when tapping the SlimSelect trigger
      const ssMain = e.target.closest(".ss-main")
      if (ssMain) {
        e.preventDefault()
        ssMain.click()
        return
      }

      // Select option when tapping a dropdown item
      const ssOption = e.target.closest(".ss-option")
      if (ssOption) {
        e.preventDefault()
        ssOption.click()
        return
      }
    },
    { passive: false },
  )

  // Prevent native <select> from opening when SlimSelect manages it
  const preventNativeSelect = (e) => {
    const select = e.target.closest("select")
    if (
      select &&
      select.nextElementSibling &&
      select.nextElementSibling.classList.contains("ss-main")
    ) {
      e.preventDefault()
      e.stopImmediatePropagation()
    }
  }
  document.addEventListener("touchstart", preventNativeSelect, { capture: true, passive: false })
  document.addEventListener("touchend", preventNativeSelect, { capture: true, passive: false })
})()
